import type { ColDef } from "ag-grid-community";

interface ColumnHelper<TData> {
  /**
   * Type-checked accessor by field name. The cell renderer's `value` is typed
   * as `TData[K]`.
   */
  accessor<K extends keyof TData & string>(
    field: K,
    opts?: Omit<ColDef<TData, TData[K]>, "field" | "valueGetter">,
  ): ColDef<TData, TData[K]>;
}

/**
 * Ergonomic factories for column shapes: typed field accessor,
 * Returns plain AG Grid `ColDef`s, so callers can mix helper
 * output freely with hand-written column defs when they
 * need features the helper doesn't shortcut.
 *
 * Inspired by Tanstack Table's createColumnHelper
 */
export function createColumnHelper<TData>(): ColumnHelper<TData> {
  return {
    accessor(field, opts) {
      // ColDef.field is typed as the recursive nested-path union ColDefField<TData, TValue>,
      // so a plain string key can't be assigned without going through unknown.
      return { field, ...opts } as unknown as ColDef<
        TData,
        TData[typeof field]
      >;
    },
  };
}
